import type { Writable } from "svelte/store";
import { writable } from "svelte/store";
import * as filepath from "./filepath";
import type { GDriveConfig } from "./fs";
import { createGDriveConfig, FS } from "./fs";
import { FsDir } from "./fsentry";
import type { HttpProgress } from "./http";
import { captureEvent, telegramNotify } from "./http";
import { lsKeyGoogleAccount } from "./user";
import { sleep, throwIf } from "./util";

// Public OAuth web client. JavaScript origins: http://localhost:9313 and https://filerion.com.
// https://developers.google.com/identity/oauth2/web/guides/use-token-model
const googleClientID = "";

const driveScope = "https://www.googleapis.com/auth/drive";
const driveApi = "https://www.googleapis.com/drive/v3";
const driveUpload = "https://www.googleapis.com/upload/drive/v3/files";
const folderMime = "application/vnd.google-apps.folder";
// Multipart upload accepts about 5MB including metadata. Larger files use one resumable PUT.
const uploadSimpleMax = 4 * 1024 * 1024;

export const prop_gdrive_mime = "mimeType";
export const prop_gdrive_md5 = "md5Checksum";

interface GDriveExport {
  mime: string;
  ext: string;
}

const exportByMime: Record<string, GDriveExport> = {
  "application/vnd.google-apps.document": {
    mime: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ext: ".docx",
  },
  "application/vnd.google-apps.spreadsheet": {
    mime: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ext: ".xlsx",
  },
  "application/vnd.google-apps.presentation": {
    mime: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    ext: ".pptx",
  },
  "application/vnd.google-apps.drawing": {
    mime: "image/svg+xml",
    ext: ".svg",
  },
  "application/vnd.google-apps.script": {
    mime: "application/vnd.google-apps.script+json",
    ext: ".json",
  },
};

export interface GDriveFile {
  id?: string;
  name?: string;
  mimeType?: string;
  size?: string;
  modifiedTime?: string;
  md5Checksum?: string;
  trashed?: boolean;
}

interface GoogleTokenResponse {
  access_token?: string;
  expires_in?: number;
  error?: string;
  error_description?: string;
}

interface GoogleTokenClient {
  requestAccessToken: (over?: { prompt?: string; hint?: string }) => void;
}

declare global {
  interface Window {
    google?: {
      accounts?: {
        oauth2?: {
          initTokenClient: (cfg: {
            client_id: string;
            scope: string;
            callback: (resp: GoogleTokenResponse) => void;
            error_callback?: (err: { type?: string; message?: string }) => void;
          }) => GoogleTokenClient;
        };
      };
    };
  }
}

class DriveStatusError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

export function gdriveIsDir(mime: string): boolean {
  return mime == folderMime;
}

export function gdriveExport(mime: string): GDriveExport | null {
  return exportByMime[mime] || null;
}

export function gdriveListQuery(parentId: string): string {
  let id = parentId.replace(/\\/g, "\\\\").replace(/'/g, "\\'");
  return `'${id}' in parents and trashed = false`;
}

export function gdriveDownloadURL(id: string, mime: string): string {
  let file = `${driveApi}/files/${encodeURIComponent(id)}`;
  let exp = gdriveExport(mime);
  if (exp) {
    return `${file}/export?mimeType=${encodeURIComponent(exp.mime)}`;
  }
  if (mime.startsWith("application/vnd.google-apps.")) {
    throw new Error("cannot download this Google file");
  }
  return `${file}?alt=media`;
}

export function gdriveErrorMessage(body: string, status: number): string {
  if (body) {
    try {
      let js = JSON.parse(body);
      let msg = js.error && js.error.message;
      if (msg) {
        return msg;
      }
    } catch {
      // not JSON
    }
  }
  if (status) {
    return `Google Drive ${status}`;
  }
  return "Google Drive request failed";
}

export function gdriveFilesToWire(files: GDriveFile[]): any[][] {
  let wires: any[][] = [];
  for (let file of files) {
    if (!file.id || !file.name || file.trashed) {
      continue;
    }
    if (file.name == "." || file.name == "..") {
      continue;
    }
    let mime = file.mimeType || "";
    let isDir = gdriveIsDir(mime);
    let size = -1;
    if (!isDir) {
      size = file.size ? Number(file.size) : 0;
      if (!Number.isFinite(size) || size < 0) {
        size = 0;
      }
    }
    let lastMod = file.modifiedTime ? Date.parse(file.modifiedTime) : 0;
    if (Number.isNaN(lastMod)) {
      lastMod = 0;
    }
    let meta: any[] = [file.id, file.name, size, lastMod, isDir ? [] : null];
    if (mime) {
      meta.push(prop_gdrive_mime, mime);
    }
    if (!isDir && file.md5Checksum) {
      meta.push(prop_gdrive_md5, file.md5Checksum);
    }
    wires.push(meta);
  }
  return wires;
}

let gsiPromise: Promise<void> | null = null;
let tokenClient: GoogleTokenClient | null = null;
let tokenWait: {
  resolve: (resp: GoogleTokenResponse) => void;
  reject: (err: Error) => void;
} | null = null;
let tokenGate: Promise<unknown> = Promise.resolve();

function loadGsi(): Promise<void> {
  if (window.google?.accounts?.oauth2) {
    return Promise.resolve();
  }
  if (gsiPromise) {
    return gsiPromise;
  }
  gsiPromise = new Promise((resolve, reject) => {
    let s = document.createElement("script");
    s.src = "https://accounts.google.com/gsi/client";
    s.async = true;
    s.onload = () => {
      if (window.google?.accounts?.oauth2) {
        resolve();
        return;
      }
      gsiPromise = null;
      reject(new Error("could not load Google sign-in"));
    };
    s.onerror = () => {
      gsiPromise = null;
      reject(new Error("could not load Google sign-in"));
    };
    document.head.appendChild(s);
  });
  return gsiPromise;
}

function signInError(err: { type?: string; message?: string }): Error {
  if (err.type == "popup_closed") {
    return new Error("Google sign-in was closed");
  }
  if (err.type == "popup_failed_to_open") {
    return new Error("Google sign-in popup was blocked");
  }
  return new Error(err.message || err.type || "Google sign-in failed");
}

function tokenError(resp: GoogleTokenResponse): Error {
  if (resp.error == "access_denied") {
    return new Error("Google sign-in was denied");
  }
  return new Error(resp.error_description || resp.error || "Google sign-in failed");
}

function requestGoogleToken(prompt: string, hint?: string): Promise<GoogleTokenResponse> {
  let run = tokenGate.then(() => requestGoogleTokenNow(prompt, hint));
  tokenGate = run.then(
    () => {},
    () => {},
  );
  return run;
}

async function requestGoogleTokenNow(prompt: string, hint?: string): Promise<GoogleTokenResponse> {
  throwIf(!googleClientID, "Google Drive sign-in is not configured");
  await loadGsi();
  let oauth = window.google?.accounts?.oauth2;
  if (!oauth) {
    throw new Error("could not load Google sign-in");
  }
  let client = tokenClient;
  if (!client) {
    client = oauth.initTokenClient({
      client_id: googleClientID,
      scope: driveScope,
      callback: (resp) => {
        let wait = tokenWait;
        tokenWait = null;
        if (!wait) {
          return;
        }
        if (resp.error || !resp.access_token) {
          wait.reject(tokenError(resp));
          return;
        }
        wait.resolve(resp);
      },
      error_callback: (err) => {
        let wait = tokenWait;
        tokenWait = null;
        wait?.reject(signInError(err));
      },
    });
    tokenClient = client;
  }
  return new Promise((resolve, reject) => {
    tokenWait = { resolve, reject };
    let over: { prompt?: string; hint?: string } = { prompt };
    if (hint) {
      over.hint = hint;
    }
    try {
      client.requestAccessToken(over);
    } catch (e) {
      tokenWait = null;
      reject(e instanceof Error ? e : new Error(String(e)));
    }
  });
}

async function tokenFresh(c: GDriveConfig): Promise<boolean> {
  if (!c.accessToken || !c.tokenExpires) {
    return false;
  }
  if (Date.now() > new Date(c.tokenExpires).getTime() - 60_000) {
    return false;
  }
  let dev = await import("./dev");
  if (dev.corruptGoogleDrive) {
    dev.setCorruptGoogleDrive(false);
    return false;
  }
  return true;
}

const tokenRefresh = new Map<string, Promise<void>>();

export async function gdriveEnsureToken(c: GDriveConfig): Promise<string> {
  if (await tokenFresh(c)) {
    return c.accessToken;
  }
  let key = c.email || c.fsID;
  let pending = tokenRefresh.get(key);
  if (!pending) {
    pending = refreshToken(c).finally(() => {
      tokenRefresh.delete(key);
    });
    tokenRefresh.set(key, pending);
  }
  await pending;
  return c.accessToken;
}

async function refreshToken(c: GDriveConfig): Promise<void> {
  let resp = await requestGoogleToken("", c.email);
  c.accessToken = resp.access_token || "";
  let seconds = resp.expires_in || 3600;
  c.tokenExpires = new Date(Date.now() + seconds * 1000).toISOString();
  throwIf(!c.accessToken, "Google sign-in failed");
}

async function gdriveAccount(c: GDriveConfig): Promise<{ email: string; name: string }> {
  let js = await driveJSON(c, "GET", `${driveApi}/about?fields=user(emailAddress,displayName)`);
  let user = (js.user || {}) as { emailAddress?: string; displayName?: string };
  return {
    email: user.emailAddress || "",
    name: user.displayName || "",
  };
}

export async function connectGDrive(): Promise<GDriveConfig> {
  let resp = await requestGoogleToken("select_account");
  let c = createGDriveConfig();
  c.accessToken = resp.access_token || "";
  let seconds = resp.expires_in || 3600;
  c.tokenExpires = new Date(Date.now() + seconds * 1000).toISOString();
  let account = await gdriveAccount(c);
  c.email = account.email;
  c.name = account.name || account.email || "Google Drive";
  if (account.email) {
    localStorage.setItem(lsKeyGoogleAccount, JSON.stringify({ email: account.email, displayName: account.name }));
  }
  let who = account.email || account.name || c.name;
  telegramNotify(`New Google Drive connection for ${who}`);
  captureEvent("gdrive-login", { email: account.email });
  console.log("connectGDrive:", account.email);
  return c;
}

async function driveJSON(c: GDriveConfig, method: string, url: string, body?: string): Promise<any> {
  let last = "Google Drive request failed";
  for (let attempt = 0; attempt < 4; attempt++) {
    let token = await gdriveEnsureToken(c);
    let headers: Record<string, string> = {
      Authorization: `Bearer ${token}`,
    };
    if (body != undefined) {
      headers["Content-Type"] = "application/json";
    }
    let rsp: Response;
    try {
      rsp = await fetch(url, { method, headers, body });
    } catch (e: any) {
      last = e.toString();
      await sleep(500 * (attempt + 1));
      continue;
    }
    let text = await rsp.text();
    if (rsp.status == 401 && attempt == 0) {
      c.accessToken = "";
      c.tokenExpires = "";
      continue;
    }
    if ((rsp.status == 429 || rsp.status >= 500) && attempt < 3) {
      last = gdriveErrorMessage(text, rsp.status);
      await sleep(1000 * (attempt + 1));
      continue;
    }
    if (!rsp.ok) {
      throw new DriveStatusError(rsp.status, gdriveErrorMessage(text, rsp.status));
    }
    if (!text) {
      return {};
    }
    try {
      return JSON.parse(text);
    } catch {
      return {};
    }
  }
  throw new Error(last);
}

function driveSend(
  method: string,
  url: string,
  token: string,
  data: Blob | undefined,
  onProgress: HttpProgress | undefined,
  responseType: XMLHttpRequestResponseType,
  headers: Record<string, string>,
): Promise<XMLHttpRequest> {
  return new Promise((resolve, reject) => {
    let xhr = new XMLHttpRequest();
    xhr.open(method, url);
    xhr.responseType = responseType;
    xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    for (let key of Object.keys(headers)) {
      xhr.setRequestHeader(key, headers[key]);
    }
    xhr.onload = () => {
      resolve(xhr);
    };
    xhr.onerror = () => {
      reject(new Error("Google Drive request failed"));
    };
    const start = Date.now();
    let progress = (ev: ProgressEvent) => {
      if (!onProgress) {
        return;
      }
      onProgress(ev.loaded, ev.lengthComputable ? ev.total : 0, Date.now() - start);
    };
    if (method == "POST" || method == "PUT" || method == "PATCH") {
      xhr.upload.onprogress = progress;
    } else {
      xhr.onprogress = progress;
    }
    if (data) {
      xhr.send(data);
    } else {
      xhr.send();
    }
  });
}

async function driveXhrError(xhr: XMLHttpRequest): Promise<void> {
  if (xhr.status >= 200 && xhr.status < 300) {
    return;
  }
  let text = "";
  if (xhr.responseType == "blob" && xhr.response instanceof Blob) {
    text = await xhr.response.text();
  } else if (xhr.responseType == "" || xhr.responseType == "text") {
    text = xhr.responseText;
  }
  throw new DriveStatusError(xhr.status, gdriveErrorMessage(text, xhr.status));
}

function hexBoundary(): string {
  let bytes = crypto.getRandomValues(new Uint8Array(18));
  let s = "";
  for (let b of bytes) {
    s += b.toString(16).padStart(2, "0");
  }
  return "filerion" + s;
}

async function gdriveMultipart(
  token: string,
  name: string,
  parentId: string,
  file: Blob,
  onProgress: HttpProgress,
): Promise<void> {
  let boundary = hexBoundary();
  let type = file.type || "application/octet-stream";
  let meta = JSON.stringify({ name: name, parents: [parentId] });
  let head =
    `--${boundary}\r\n` +
    `Content-Type: application/json; charset=UTF-8\r\n\r\n` +
    `${meta}\r\n` +
    `--${boundary}\r\n` +
    `Content-Type: ${type}\r\n\r\n`;
  let body = new Blob([head, file, `\r\n--${boundary}--`]);
  let xhr = await driveSend("POST", `${driveUpload}?uploadType=multipart&fields=id`, token, body, onProgress, "", {
    "Content-Type": `multipart/related; boundary=${boundary}`,
  });
  await driveXhrError(xhr);
}

async function gdriveResumable(
  token: string,
  name: string,
  parentId: string,
  file: Blob,
  onProgress: HttpProgress,
): Promise<void> {
  let type = file.type || "application/octet-stream";
  let rsp = await fetch(`${driveUpload}?uploadType=resumable&fields=id`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json; charset=UTF-8",
      "X-Upload-Content-Type": type,
      "X-Upload-Content-Length": String(file.size),
    },
    body: JSON.stringify({ name: name, parents: [parentId] }),
  });
  let text = await rsp.text();
  if (!rsp.ok) {
    throw new DriveStatusError(rsp.status, gdriveErrorMessage(text, rsp.status));
  }
  let location = rsp.headers.get("Location");
  if (!location) {
    throw new Error("Google Drive upload did not return a session");
  }
  let xhr = await driveSend("PUT", location, token, file, onProgress, "", {
    "Content-Type": type,
    "Content-Range": `bytes 0-${file.size - 1}/${file.size}`,
  });
  await driveXhrError(xhr);
}

async function gdriveList(c: GDriveConfig, parentId: string): Promise<GDriveFile[]> {
  let files: GDriveFile[] = [];
  let pageToken = "";
  for (let page = 0; page < 50; page++) {
    let url = new URL(`${driveApi}/files`);
    url.searchParams.set("q", gdriveListQuery(parentId));
    url.searchParams.set("pageSize", "1000");
    url.searchParams.set("fields", "nextPageToken,files(id,name,mimeType,size,modifiedTime,md5Checksum,trashed)");
    url.searchParams.set("spaces", "drive");
    if (pageToken) {
      url.searchParams.set("pageToken", pageToken);
    }
    let js = await driveJSON(c, "GET", url.toString());
    files.push(...((js.files || []) as GDriveFile[]));
    let next = (js.nextPageToken as string) || "";
    if (!next || next == pageToken) {
      return files;
    }
    pageToken = next;
  }
  throw new Error("too many files in this folder");
}

class FileSystemGDrive extends FS {
  supportsDirRename = true;

  private rootId = "";

  constructor(config: GDriveConfig) {
    super(config);
  }

  get gdriveConfig(): GDriveConfig {
    return this.config as GDriveConfig;
  }

  async readDir(dirPath: string, force: boolean, progress: Writable<any>): Promise<FsDir> {
    throwIf(dirPath == "", "empty dir");
    return this.loadDir(dirPath, force, progress, async () => {
      let parentId: string;
      if (dirPath == "/") {
        parentId = await this.rootFileId();
      } else {
        await this.readParentDirs(dirPath, force, progress);
        parentId = this.entryId(dirPath);
      }
      let files = await gdriveList(this.gdriveConfig, parentId);
      return FsDir.fromWire(dirPath, gdriveFilesToWire(files));
    });
  }

  // Previews cannot send Authorization, so the bytes come back as a blob URL.
  async getPublicURL(path: string): Promise<string> {
    let blob = await this.downloadFile(path, () => {});
    return URL.createObjectURL(blob);
  }

  async downloadFile(path: string, httpProgress: HttpProgress): Promise<Blob> {
    let id = await this.idOf(path);
    let url = gdriveDownloadURL(id, this.mimeOf(path));
    let xhr = await this.send("GET", url, undefined, httpProgress, "blob", {}, true);
    return xhr.response as Blob;
  }

  downloadName(path: string): string {
    let name = filepath.base(filepath.stripDirSlash(path));
    let exp = gdriveExport(this.mimeOf(path));
    if (exp && !name.toLowerCase().endsWith(exp.ext)) {
      return name + exp.ext;
    }
    return name;
  }

  async uploadFile(path: string, file: Blob, httpProgress: HttpProgress): Promise<void> {
    let name = filepath.base(path);
    throwIf(!name, "name is required");
    let parentId = await this.parentId(path);
    await this.putNew(parentId, name, file, httpProgress, true);
  }

  async rename(path: string, newPath: string): Promise<void> {
    throwIf(path == newPath, "path must be != newPath");
    let src = filepath.stripDirSlash(path);
    let dst = filepath.stripDirSlash(newPath);
    throwIf(src == "/" || dst == "/", "path is required");
    let name = filepath.base(dst);
    throwIf(!name, "name is required");
    let id = await this.idOf(src);
    let srcDir = filepath.dir(src);
    let dstDir = filepath.dir(dst);
    let url = `${driveApi}/files/${encodeURIComponent(id)}`;
    if (srcDir != dstDir) {
      let add = await this.parentId(dst);
      let remove = await this.idOf(srcDir);
      url += `?addParents=${encodeURIComponent(add)}&removeParents=${encodeURIComponent(remove)}`;
    }
    await driveJSON(this.gdriveConfig, "PATCH", url, JSON.stringify({ name: name }));
  }

  async createDirectory(dirPath: string): Promise<void> {
    let path = filepath.stripDirSlash(dirPath);
    throwIf(path == "" || path == "/", "name is required");
    let parentPath = filepath.dir(path);
    if (parentPath == "") {
      parentPath = "/";
    }
    if (!this.dirs.get(parentPath)) {
      await this.readDir(parentPath, false, writable(""));
    }
    let name = filepath.base(path);
    let listing = this.dirs.get(parentPath);
    if (!listing) {
      throw new Error("missing parent");
    }
    let n = listing.count();
    for (let i = 0; i < n; i++) {
      if (listing.entryName(i) == name && listing.entryIsDir(i)) {
        return;
      }
    }
    let parent = await this.idOf(parentPath);
    await driveJSON(
      this.gdriveConfig,
      "POST",
      `${driveApi}/files`,
      JSON.stringify({
        name: name,
        mimeType: folderMime,
        parents: [parent],
      }),
    );
  }

  async deleteFile(path: string): Promise<void> {
    let clean = filepath.stripDirSlash(path);
    throwIf(clean == "" || clean == "/", "path is required");
    let id = await this.idOf(clean);
    await driveJSON(
      this.gdriveConfig,
      "PATCH",
      `${driveApi}/files/${encodeURIComponent(id)}`,
      JSON.stringify({ trashed: true }),
    );
  }

  private async putNew(
    parentId: string,
    name: string,
    file: Blob,
    httpProgress: HttpProgress,
    retry: boolean,
  ): Promise<void> {
    let token = await gdriveEnsureToken(this.gdriveConfig);
    try {
      if (file.size <= uploadSimpleMax) {
        await gdriveMultipart(token, name, parentId, file, httpProgress);
      } else {
        await gdriveResumable(token, name, parentId, file, httpProgress);
      }
    } catch (e) {
      if (retry && e instanceof DriveStatusError && e.status == 401) {
        this.gdriveConfig.accessToken = "";
        this.gdriveConfig.tokenExpires = "";
        await this.putNew(parentId, name, file, httpProgress, false);
        return;
      }
      throw e;
    }
  }

  private async send(
    method: string,
    url: string,
    data: Blob | undefined,
    httpProgress: HttpProgress | undefined,
    responseType: XMLHttpRequestResponseType,
    headers: Record<string, string>,
    retry: boolean,
  ): Promise<XMLHttpRequest> {
    let token = await gdriveEnsureToken(this.gdriveConfig);
    let xhr = await driveSend(method, url, token, data, httpProgress, responseType, headers);
    if (retry && xhr.status == 401) {
      this.gdriveConfig.accessToken = "";
      this.gdriveConfig.tokenExpires = "";
      return this.send(method, url, data, httpProgress, responseType, headers, false);
    }
    await driveXhrError(xhr);
    return xhr;
  }

  private async rootFileId(): Promise<string> {
    if (this.rootId) {
      return this.rootId;
    }
    let js = await driveJSON(this.gdriveConfig, "GET", `${driveApi}/files/root?fields=id`);
    let id = (js.id as string) || "";
    throwIf(!id, "Google Drive did not return a root");
    this.rootId = id;
    return id;
  }

  private async parentId(path: string): Promise<string> {
    let dir = filepath.dir(filepath.stripDirSlash(path));
    if (dir == "" || dir == "/") {
      return this.rootFileId();
    }
    if (!this.dirs.get(dir)) {
      await this.readDir(dir, false, writable(""));
    }
    return this.entryId(dir);
  }

  private async idOf(path: string): Promise<string> {
    path = filepath.stripDirSlash(path);
    if (path == "" || path == "/") {
      return this.rootFileId();
    }
    try {
      return this.entryId(path);
    } catch {
      let parent = filepath.dir(path);
      if (parent == "") {
        parent = "/";
      }
      await this.readDir(parent, false, writable(""));
      return this.entryId(path);
    }
  }

  private entryId(path: string): string {
    path = filepath.stripDirSlash(path);
    let found = this.findEntries(path);
    return found.dir.entryId(found.entries[0]);
  }

  private mimeOf(path: string): string {
    try {
      let found = this.findEntries(filepath.stripDirSlash(path));
      return found.dir.entryMeta(found.entries[0], prop_gdrive_mime) || "";
    } catch {
      return "";
    }
  }
}

export function makeFileSystemGDrive(c: GDriveConfig): FileSystemGDrive {
  return new FileSystemGDrive(c);
}
